import { useParams } from 'react-router-dom';
import { Breadcrumbs, Grid, Paper, Typography } from '@material-ui/core';
import {
  Content,
  Header,
  HeaderLabel,
  InfoCard,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import useAsync from 'react-use/lib/useAsync';
import { compressSchema, getParamDescriptionMap } from '../../util';
import {
  MoveAvroSchema,
  TrackingValidation,
  drEventTaxonomyReferenceDataProxyApiRef,
  EventsDetailedMap,
} from '../../api';
import { useApi } from '@backstage/core-plugin-api';

type ParamDetailComponentProp = {
  eventDetails?: MoveAvroSchema;
  eventName?: string;
  paramName?: string;
};

export const ParamDetailComponent = (props: ParamDetailComponentProp) => {
  if (props.eventDetails) {
    const paramDescriptionMap = getParamDescriptionMap(props.eventDetails);
    const flattenedEventDetails = compressSchema(props.eventDetails);
    let currentEvent: TrackingValidation | undefined;
    if (props.paramName !== undefined) {
      currentEvent = flattenedEventDetails.get(props.paramName);
      if (currentEvent === undefined) {
        currentEvent = flattenedEventDetails.get(`properties.${props.paramName}`);
      }
    } else {
      currentEvent = undefined;
    }
    const exampleColumn: TableColumn[] = [{ title: 'Example Values', field: 'examples' }];
    const exampleData: any[] = [];
    currentEvent?.examples?.map(example => exampleData.push({ examples: <div>{example}</div> }));
    return (
      <Grid container spacing={3} direction="column">
        <Grid item>
          {exampleData.length > 0 ? (
            <Table
              title={`"${props.paramName}" parameter`}
              subtitle={paramDescriptionMap.get(props.paramName as string)}
              columns={exampleColumn}
              data={exampleData}
              options={{ search: false, paging: false, padding: 'dense' }}
            />
          ) : (
            <Table
              title={`"${props.paramName}" parameter`}
              subtitle={paramDescriptionMap.get(props.paramName as string)}
              columns={exampleColumn}
              data={[
                {
                  examples: (
                    <div>
                      There are no examples provided for this event parameter. Please reach out to #data-reliability if
                      you'd like further guidance.
                    </div>
                  ),
                },
              ]}
              options={{ search: false, paging: false, padding: 'dense' }}
            />
          )}
        </Grid>
        <Grid item>
          {currentEvent?.rules?.length !== undefined && currentEvent?.rules?.length > 0 ? (
            <InfoCard>
              <Typography color="textPrimary" variant="h6">
                Rules
              </Typography>
              {currentEvent?.rules?.map((rule, idx) => (
                <div key={idx}>{rule.validationErrorMessage}</div>
              ))}
            </InfoCard>
          ) : (
            <InfoCard>
              <Typography color="textPrimary" variant="h6">
                Rules
              </Typography>
              {"There are no rules provided for this event parameter. Please reach out to #data-reliability if you'd\n" +
                'like further guidance.'}
            </InfoCard>
          )}
        </Grid>
      </Grid>
    );
  }
  return (
    <div>
      <Paper>{`PARAM: ${props.paramName} DETAILS NOT FOUND`}</Paper>
    </div>
  );
};

export const ParamComponent = ({ mockData }: any) => {
  const params = useParams();
  const apiClient = useApi(drEventTaxonomyReferenceDataProxyApiRef);
  const actualEventName = params?.eventID?.replace('Event', '');
  const { value, loading, error } = useAsync(async () => {
    if (mockData !== undefined) {
      return mockData.data;
    }
    return apiClient.get<EventsDetailedMap>(`digital-analytics-tracking-validator/v1/event/${actualEventName}`);
  });

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  }
  const eventDetails = value !== undefined ? value[actualEventName as string] : undefined;
  return (
    <Page themeId="tool">
      <Header
        title="Event Taxonomy Reference Data (ETRD)"
        subtitle="Streamline feature tracking with event taxonomy-related guidance"
      >
        <HeaderLabel label="Owner" value="Data Reliability" />
        <HeaderLabel label="Slack" value="#data-reliability" />
      </Header>
      <Content>
        <Grid direction="column" spacing={3} container>
          <Grid item>
            <Breadcrumbs>
              <Link to="/dr-event-taxonomy-reference-data">Home</Link>
              <Link to="/dr-event-taxonomy-reference-data/events">Events</Link>
              <Link to={`/dr-event-taxonomy-reference-data/events/${params.eventID}`}>{params.eventID}</Link>
              <Link to={`/dr-event-taxonomy-reference-data/events/${params.eventID}/${params.paramID}`}>
                {params.paramID}
              </Link>
            </Breadcrumbs>
          </Grid>
          <Grid item>
            <ParamDetailComponent eventDetails={eventDetails} paramName={params.paramID} eventName={params.eventID} />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
